import { UseFormReturn } from 'react-hook-form'
import { Dispatch, SetStateAction, useContext, useEffect, useRef } from 'react'
import { isEmpty, memoize, omit } from 'lodash'
import { Option } from '@organisms/Select'
import { FilterState } from '@hooks/useItemsFilters'
import { TopControllersContext } from '@modals/RiskMapModal'
import isEmptyForm from '@modals/RiskMapModal/Dangers/utils/isEmptyForm'
import { selectField } from '@templates/AddNewItemModal/templates'
import INJURY_POSSIBILITIES from '@const/injuryPossibilities'
import SEVERITY_DEGREES from '@const/severityDegrees'
import { StateType } from '@app-types/riskMapFields'
import useDangerWorkPlacesField from './useDangerWorkPlacesField'
import {
  getDangerHazardName,
  getEventName,
  getHardGradeName,
  getKindName,
  getObjectName,
  getProbabilityName,
  getWorkPlaceName,
} from './nameUtils'
import useDangerObjectsField from './useDangerObjectsField'
import useDangerKindsField from './useDangerKindsField'
import useDangerHazardsField from './useDangerHazardsField'
import useDangerEventsField from './useDangerEventsField'
import getFilters from './utils/getFilters'

const getFilterValue = (field: Option<string> | undefined) =>
  !field || field?.withManualEntryText ? undefined : field.value

type OptionalParams = {
  dangersSetFilters?: Dispatch<SetStateAction<FilterState>>
  index?: number
  isRequiredForm?: boolean
  inSelectDangersModal?: boolean
  ignoreJobPositions?: boolean
  initFilters?: StateType
}

const useCommonRiskMapFields = (
  { watch, setValue, formState }: UseFormReturn,
  optionalParams?: OptionalParams
) => {
  const {
    index,
    dangersSetFilters,
    isRequiredForm: isRequiredFormExternal,
    inSelectDangersModal = false,
    ignoreJobPositions = false,
    initFilters,
  } = optionalParams || {}

  const skip = isEmptyForm(
    inSelectDangersModal
      ? formState.dirtyFields
      : (formState.dirtyFields.dangerDTOs || [])[index || 0] || {}
  )

  const { enabledEtn } = useContext(TopControllersContext)

  const workPlaceName = getWorkPlaceName(index)
  const objectName = getObjectName(index)
  const kindName = getKindName(index)
  const dangerHazardName = getDangerHazardName(index)
  const eventName = getEventName(index)

  const workPlace = watch(workPlaceName) as Option<string> | undefined
  const object = watch(objectName) as Option<string> | undefined
  const kind = watch(kindName) as Option<string> | undefined
  const hazard = watch(dangerHazardName) as Option<string> | undefined
  const event = watch(eventName) as Option<string> | undefined

  const isRequiredForm =
    typeof isRequiredFormExternal === 'boolean'
      ? isRequiredFormExternal
      : !!(workPlace || kind || hazard || event)

  const getOnOneItem = memoize((name) => (value: Option) => {
    setValue(name, value)
  })

  const { field: dangerWorkPlacesField, setFilters: workPlaceSetFilters } =
    useDangerWorkPlacesField({
      params: {
        name: workPlaceName,
        isRequired: isRequiredForm,
      },
      onOneItem: getOnOneItem(workPlaceName),
      skip,
      initFilters,
    })
  const { field: dangersObjects, setFilters: dangersObjectsSetFilters } =
    useDangerObjectsField({
      params: {
        name: objectName,
        isRequired: isRequiredForm,
      },
      onOneItem: getOnOneItem(objectName),
      skip,
      initFilters,
    })
  const { field: dangerKindsField, setFilters: dangerKindsSetFilters } =
    useDangerKindsField({
      params: {
        name: kindName,
      },
      skip,
      initFilters,
    })
  const { field: dangerHazardsField, setFilters: dangerHazardsSetFilters } =
    useDangerHazardsField({
      params: {
        name: dangerHazardName,
      },
      onOneItem: getOnOneItem(dangerHazardName),
      skip,
      initFilters,
    })
  const { field: dangerEventsField, setFilters: dangerEventsSetFilters } =
    useDangerEventsField({
      params: {
        name: eventName,
      },
      onOneItem: getOnOneItem(eventName),
      skip,
      initFilters,
    })

  const probabilityField = () =>
    selectField({
      isRequired: isRequiredForm,
      withoutFetchOptions: true,
      options: INJURY_POSSIBILITIES,
      label: 'Вероятность получения травмы',
      name: getProbabilityName(index),
    })

  const hardGradeField = () =>
    selectField({
      isRequired: isRequiredForm,
      withoutFetchOptions: true,
      options: SEVERITY_DEGREES,
      label: 'Степень тяжести',
      name: getHardGradeName(index),
    })

  const prevRelationsRef = useRef<any>(null)

  const relations = {
    ...getFilters({
      workPlace: getFilterValue(workPlace),
      kind: getFilterValue(kind),
      hazard: getFilterValue(hazard),
      event: getFilterValue(event),
      object: getFilterValue(object),
    }),
    etn: enabledEtn ? 'true' : undefined,
  }
  const stringRelations = JSON.stringify(relations)

  useEffect(() => {
    if (prevRelationsRef.current === stringRelations) return

    workPlaceSetFilters((prev) => ({
      ...prev,
      relations,
    }))
    dangersObjectsSetFilters((prev) => ({
      ...prev,
      relations,
    }))
    dangerKindsSetFilters((prev) => ({
      ...prev,
      relations,
    }))
    dangerHazardsSetFilters((prev) => ({
      ...prev,
      relations,
    }))
    dangerEventsSetFilters((prev) => ({
      ...prev,
      relations,
    }))

    if (!isEmpty(omit(relations, 'etn'))) {
      dangersSetFilters?.({
        ...relations,
        ignoreJobPositions,
      })
    }

    prevRelationsRef.current = stringRelations
  }, [stringRelations])

  return {
    dangerWorkPlacesField,
    dangersObjects,
    dangerKindsField,
    dangerHazardsField,
    dangerEventsField,
    probabilityField,
    hardGradeField,
  }
}

export default useCommonRiskMapFields
